/**
 * Sub-processors — static public page, no auth required.
 * The list of third parties that process personal data on Enereg's behalf, linked from the
 * Privacy Policy (§7) and the marketing site footer. Keep this in sync with the Record of
 * Processing Activities (docs/data-protection/ropa.md) and Privacy Policy §7/§12.
 */
import { Zap } from "lucide-react";
import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "July 14, 2026";

type Processor = {
  name: string;
  purpose: string;
  data: string;
  region: string;
};

const PROCESSORS: Processor[] = [
  {
    name: "Supabase",
    purpose: "Authentication (OAuth + magic-link sign-in emails), session identity, the waitlist/lead table, and the regulatory knowledge base (vector search).",
    data: "Email, name, OAuth identifiers; waitlist details (company, country, capacity); checkup records.",
    region: "United States",
  },
  {
    name: "Anthropic (Claude API)",
    purpose: "Extracting structured fields from documents you upload, and drafting evaluation guidance.",
    data: "Document content and project field values (not used to train models).",
    region: "United States",
  },
  {
    name: "OpenAI (Embeddings API)",
    purpose: "Vector embeddings for the regulatory knowledge base.",
    data: "Public regulatory documents only — not your private project data.",
    region: "United States",
  },
  {
    name: "Fly.io",
    purpose: "Application hosting — our server and primary database.",
    data: "All data at rest, encrypted, in the Johannesburg region.",
    region: "South Africa (Johannesburg)",
  },
  {
    name: "Google Cloud Platform",
    purpose: "Google sign-in and Google Drive access — only when you choose to connect a drive.",
    data: "OAuth tokens (encrypted) and metadata/content of the folder you select (read-only).",
    region: "United States / global",
  },
  {
    name: "Microsoft Azure",
    purpose: "Microsoft sign-in and OneDrive access — only when you choose to connect a drive.",
    data: "OAuth tokens (encrypted) and metadata/content of the folder you select (read-only).",
    region: "United States / global",
  },
  {
    name: "Vercel",
    purpose: "Hosting the marketing website (enereg.com). Static pages only.",
    data: "Standard web server logs. No analytics or tracking cookies.",
    region: "United States / global",
  },
];

export function SubProcessors() {
  return (
    <div className="min-h-screen bg-surface-secondary">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <Zap size={24} className="text-brand" />
          <span className="text-[20px] font-bold text-brand" style={{ letterSpacing: "-0.02em" }}>enereg</span>
        </Link>

        <article className="bg-surface border border-border rounded-2xl p-6 sm:p-10">
          <h1 className="text-[28px] font-bold text-text-primary mb-1" style={{ letterSpacing: "-0.02em" }}>Sub-processors</h1>
          <p className="text-[14px] text-text-tertiary mb-8">Last updated: {EFFECTIVE_DATE}</p>

          <div className="space-y-6 text-[15px] leading-relaxed text-text-secondary">
            <p>
              These are the third parties that process personal data on Enereg's behalf to run the
              platform. Each processes only the minimum data required for its function, under its own
              data-processing terms. This page is referenced by our{" "}
              <Link to="/privacy" className="text-brand hover:underline">Privacy Policy</Link> and kept
              current as our providers change.
            </p>

            <div className="space-y-4">
              {PROCESSORS.map((p) => (
                <div key={p.name} className="border border-border rounded-xl p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                    <h2 className="text-[16px] font-semibold text-text-primary">{p.name}</h2>
                    <span className="text-[13px] text-text-tertiary">{p.region}</span>
                  </div>
                  <p className="mb-1"><span className="font-medium text-text-primary">Purpose:</span> {p.purpose}</p>
                  <p><span className="font-medium text-text-primary">Data:</span> {p.data}</p>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-text-tertiary">
              We do not sell personal data, and we do not use it for advertising. Data transferred to
              providers in other countries is transferred only as necessary to provide the service.
              Questions? Email <span className="font-medium text-text-primary">team+help@enereg.com</span>.
            </p>
          </div>
        </article>

        <footer className="mt-8 text-center text-[13px] text-text-tertiary">
          <Link to="/privacy" className="hover:text-text-secondary">Privacy Policy</Link>
          <span className="mx-2">&middot;</span>
          <Link to="/terms" className="hover:text-text-secondary">Terms of Service</Link>
          <span className="mx-2">&middot;</span>
          <Link to="/" className="hover:text-text-secondary">Back to enereg</Link>
        </footer>
      </div>
    </div>
  );
}
